import { useAuth } from './AuthProvider';
import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Unset = () => (
  <span className="text-gray-400 italic">&lt;unset&gt;</span>
);

// Editable inline field — click to edit, Escape or blur to commit
const EditableField = ({ value, onEdit, multiline = false, testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const commit = () => {
    setEditing(false);
    onEdit(draft);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') commit();
    if (!multiline && e.key === 'Enter') { e.preventDefault(); commit(); }
  };

  if (editing) {
    return multiline ? (
      <textarea
        autoFocus
        className="bg-gray-700 text-gray-100 text-xs font-mono w-full rounded px-1 py-0.5 resize-none"
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        data-testid="field-input"
      />
    ) : (
      <input
        autoFocus
        className="bg-gray-700 text-gray-100 text-xs font-mono w-full rounded px-1 py-0.5"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        data-testid="field-input"
      />
    );
  }

  return (
    <span
      className="text-gray-200 cursor-pointer hover:bg-gray-700 rounded px-1 py-0.5 text-xs font-mono"
      onClick={() => { setDraft(value ?? ''); setEditing(true); }}
      data-testid={testId || 'editable-field'}
    >
      {value == null || value === '' ? <Unset /> : value}
    </span>
  );
};

// Section header with colored label
const SectionHeader = ({ label, color }) => (
  <div className={`text-xs font-bold tracking-widest uppercase mb-2 ${color} border-b border-gray-700 pb-1`}>
    {label}
  </div>
);

// Row layout for label + value
const Row = ({ label, children }) => (
  <div className="flex items-start space-x-2">
    <span className="text-gray-500 text-xs w-28 shrink-0">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const EMPTY_EDITS = {
  room: {},
  props: {},
  // _state tables
  character_state: {},
  room_state: {},
  scene_state: {},
  location_state: {},
  arc_state: {},
  relationship_state: [],
  prop_state: {},
  sequence_state: {},
  // entity tables
  characters: {},
  locations: {},
  scenes: {},
  sequences: {},
  arcs: {},
  canon_rules: {},
};

const StoryRoomEditor = ({ room }) => {
  const { session } = useAuth();
  const accessToken = session?.access_token;
  const [roomState, setRoomState] = useState(null);
  const [pendingEdits, setPendingEdits] = useState(EMPTY_EDITS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedChars, setExpandedChars] = useState({});

  const hasPendingEdits = (
    Object.keys(pendingEdits.room).length > 0 ||
    Object.keys(pendingEdits.props).length > 0 ||
    Object.keys(pendingEdits.character_state).length > 0 ||
    Object.keys(pendingEdits.room_state).length > 0 ||
    Object.keys(pendingEdits.scene_state).length > 0 ||
    Object.keys(pendingEdits.location_state).length > 0 ||
    Object.keys(pendingEdits.arc_state).length > 0 ||
    pendingEdits.relationship_state.length > 0 ||
    Object.keys(pendingEdits.prop_state).length > 0 ||
    Object.keys(pendingEdits.sequence_state).length > 0 ||
    Object.keys(pendingEdits.characters).length > 0 ||
    Object.keys(pendingEdits.locations).length > 0 ||
    Object.keys(pendingEdits.scenes).length > 0 ||
    Object.keys(pendingEdits.sequences).length > 0 ||
    Object.keys(pendingEdits.arcs).length > 0 ||
    Object.keys(pendingEdits.canon_rules).length > 0
  );

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchRoomState = useCallback(async () => {
    if (!room?.id || !accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/story/room-editor/${room.id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      setRoomState(await resp.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [room?.id, accessToken]);

  useEffect(() => { fetchRoomState(); }, [fetchRoomState]);

  // ---------------------------------------------------------------------------
  // Edit helpers — one per state table
  // ---------------------------------------------------------------------------

  const editById = (table, id, field, value) =>
    setPendingEdits(prev => ({
      ...prev,
      [table]: { ...prev[table], [id]: { ...(prev[table][id] || {}), [field]: value } },
    }));

  const editNarrative = (field, value) =>
    setPendingEdits(prev => ({ ...prev, room_state: { ...prev.room_state, [field]: value } }));

  const editScene = (field, value) =>
    setPendingEdits(prev => ({ ...prev, scene_state: { scene_id: roomState.current_scene.scene_id, ...prev.scene_state, [field]: value } }));

  // For single-row tables (scene, narrative) pending lookup uses direct object
  const scenePendingVal = (field, serverValue) => {
    if (field in pendingEdits.scene_state) return pendingEdits.scene_state[field];
    return serverValue ?? null;
  };

  const narrativePendingVal = (field, serverValue) => {
    if (field in pendingEdits.room_state) return pendingEdits.room_state[field];
    return serverValue ?? null;
  };

  const editRelationship = (charA, charB, field, value) =>
    setPendingEdits(prev => {
      const list = prev.relationship_state.filter(
        r => !(r.character_a_id === charA && r.character_b_id === charB)
      );
      const existing = prev.relationship_state.find(
        r => r.character_a_id === charA && r.character_b_id === charB
      ) || {};
      return { ...prev, relationship_state: [...list, { ...existing, character_a_id: charA, character_b_id: charB, [field]: value }] };
    });

  // Resolved display value: pending edit wins over server value
  const pendingVal = (table, id, field, serverValue) => {
    const p = pendingEdits[table]?.[id];
    if (p && field in p) return p[field];
    return serverValue ?? null;
  };

  const entityFields = (table, id, values, fields) => fields.map(field => (
    <Row key={field} label={field.replace(/_/g, ' ')}>
      <EditableField value={pendingVal(table, id, field, values[field])}
        onEdit={v => editById(table, id, field, v)} multiline
        testId={`${table}-${id}-${field}`} />
    </Row>
  ));

  const relPendingVal = (charA, charB, field, serverValue) => {
    const p = pendingEdits.relationship_state.find(
      r => r.character_a_id === charA && r.character_b_id === charB
    );
    if (p && field in p) return p[field];
    return serverValue ?? null;
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const body = {};
      if (Object.keys(pendingEdits.room).length) body.room = pendingEdits.room;
      if (Object.keys(pendingEdits.props).length) body.props = pendingEdits.props;
      if (Object.keys(pendingEdits.character_state).length)  body.character_state  = pendingEdits.character_state;
      if (Object.keys(pendingEdits.room_state).length)  body.room_state  = pendingEdits.room_state;
      if (Object.keys(pendingEdits.scene_state).length)       body.scene_state      = pendingEdits.scene_state;
      if (Object.keys(pendingEdits.location_state).length)   body.location_state   = pendingEdits.location_state;
      if (Object.keys(pendingEdits.arc_state).length)         body.arc_state        = pendingEdits.arc_state;
      if (pendingEdits.relationship_state.length)             body.relationship_state = pendingEdits.relationship_state;
      if (Object.keys(pendingEdits.prop_state).length)        body.prop_state       = pendingEdits.prop_state;
      if (Object.keys(pendingEdits.sequence_state).length)    body.sequence_state   = pendingEdits.sequence_state;
      if (Object.keys(pendingEdits.characters).length)        body.characters       = pendingEdits.characters;
      if (Object.keys(pendingEdits.locations).length)         body.locations        = pendingEdits.locations;
      if (Object.keys(pendingEdits.scenes).length)            body.scenes           = pendingEdits.scenes;
      if (Object.keys(pendingEdits.sequences).length)         body.sequences        = pendingEdits.sequences;
      if (Object.keys(pendingEdits.arcs).length)              body.arcs             = pendingEdits.arcs;
      if (Object.keys(pendingEdits.canon_rules).length)       body.canon_rules      = pendingEdits.canon_rules;

      const resp = await fetch(`${BACKEND_URL}/api/story/room-editor/${room.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${resp.status}`);
      }
      setPendingEdits(EMPTY_EDITS);
      await fetchRoomState();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------

  const handleRefresh = () => {
    if (hasPendingEdits && !window.confirm('Discard unsaved changes?')) return;
    setPendingEdits(EMPTY_EDITS);
    fetchRoomState();
  };

  const toggleChar = (id) => setExpandedChars(prev => ({ ...prev, [id]: !prev[id] }));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="bg-gray-900 text-gray-200 border border-gray-700 rounded-lg p-4 font-mono text-sm" data-testid="story-room-editor">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-green-400 font-semibold text-sm">Story Room State</h3>
        <div className="flex items-center space-x-2">
          <button onClick={handleRefresh} disabled={isLoading}
            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded">
            Refresh
          </button>
          <button onClick={handleSave} disabled={isSaving || !hasPendingEdits}
            className={`px-2 py-1 text-xs rounded ${hasPendingEdits ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}>
            {isSaving ? 'Saving…' : hasPendingEdits ? 'Save Changes*' : 'Save Changes'}
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-500 text-xs py-2">Loading…</div>}
      {error    && <div className="text-red-400 text-xs py-2">Error: {error}</div>}

      {roomState && (
        <div className="space-y-5">

          <Row label="room title">
            <EditableField value={pendingEdits.room.story_title ?? roomState.name}
              onEdit={v => setPendingEdits(prev => ({ ...prev, room: { story_title: v } }))}
              testId="room-story_title" />
          </Row>

          {/* ── SCENE ── */}
          <div>
            <SectionHeader label="SCENE" color="text-blue-400" />
            {roomState.current_scene ? (
              <div className="space-y-1 pl-2">
                {['title', 'setting_description', 'mood', 'music_brief'].map(field => (
                  <Row key={field} label={field.replace(/_/g, ' ')}>
                    <EditableField value={scenePendingVal(field, roomState.current_scene[field])}
                      onEdit={v => editScene(field, v)} multiline testId={`scene-${field}`} />
                  </Row>
                ))}
                <Row label="description">
                  <EditableField
                    value={scenePendingVal('scene_description', roomState.current_scene.scene_description)}
                    onEdit={(v) => editScene('scene_description', v)}
                    multiline testId="scene-summary-field"
                  />
                </Row>
                <Row label="goal">
                  <EditableField
                    value={scenePendingVal('scene_goal', roomState.current_scene.scene_goal)}
                    onEdit={(v) => editScene('scene_goal', v)}
                    multiline testId="editable-field"
                  />
                </Row>
                <Row label="Scene-start narration">
                  <span className="text-gray-200 text-xs whitespace-pre-wrap">
                    {roomState.scene_start_narration || 'No scene-start narration recorded yet.'}
                  </span>
                </Row>
                <Row label="Authored opening beat">
                  <EditableField
                    value={scenePendingVal('opening_beat', roomState.current_scene.opening_beat)}
                    onEdit={(v) => editScene('opening_beat', v)}
                    multiline testId="editable-field"
                  />
                </Row>
                {roomState.current_sequence && (
                  <>
                    {entityFields('sequences', roomState.current_sequence.sequence_id, roomState.current_sequence, ['title'])}
                    <Row label="pressure">
                      <EditableField
                        value={pendingVal('sequence_state', roomState.current_sequence.sequence_id, 'pressure_level',
                          roomState.current_sequence.pressure_level) != null
                          ? String(pendingVal('sequence_state', roomState.current_sequence.sequence_id, 'pressure_level',
                              roomState.current_sequence.pressure_level))
                          : null}
                        onEdit={(v) => editById('sequence_state', roomState.current_sequence.sequence_id, 'pressure_level', v ? parseInt(v, 10) : null)}
                        testId="editable-field"
                      />
                    </Row>
                    <Row label="seq status">
                      <EditableField
                        value={pendingVal('sequence_state', roomState.current_sequence.sequence_id, 'status', roomState.current_sequence.status)}
                        onEdit={(v) => editById('sequence_state', roomState.current_sequence.sequence_id, 'status', v)}
                        testId="editable-field"
                      />
                    </Row>
                    <Row label="seq goal">
                      <EditableField
                        value={pendingVal('sequences', roomState.current_sequence.sequence_id, 'goal', roomState.current_sequence.goal)}
                        onEdit={(v) => editById('sequences', roomState.current_sequence.sequence_id, 'goal', v)}
                        testId="editable-field"
                      />
                    </Row>
                    <Row label="central question">
                      <EditableField
                        value={pendingVal('sequences', roomState.current_sequence.sequence_id, 'central_question', roomState.current_sequence.central_question)}
                        onEdit={(v) => editById('sequences', roomState.current_sequence.sequence_id, 'central_question', v)}
                        testId="editable-field"
                      />
                    </Row>
                  </>
                )}
              </div>
            ) : (
              <div className="pl-2 text-xs"><Unset /></div>
            )}
          </div>

          {/* ── NARRATIVE ── */}
          <div>
            <SectionHeader label="NARRATIVE" color="text-green-400" />
            <div className="space-y-1 pl-2">
              <Row label="summary">
                <EditableField
                  value={narrativePendingVal('summary', roomState.room_summary)}
                  onEdit={(v) => editNarrative('summary', v)}
                  multiline testId="narrative-summary-field"
                />
              </Row>
              {/* Arcs — show status as editable */}
              {roomState.open_arcs?.length > 0 ? (
                <div className="mt-1">
                  <span className="text-gray-500 text-xs">arcs</span>
                  <div className="mt-1 space-y-1 pl-2">
                    {roomState.open_arcs.map((arc) => (
                      <div key={arc.arc_id} className="space-y-0.5 border border-gray-800 rounded p-1">
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 text-xs w-16 shrink-0">description</span>
                          <div className="flex-1 min-w-0">
                            <EditableField
                              value={pendingVal('arcs', arc.arc_id, 'description', arc.description)}
                              onEdit={(v) => editById('arcs', arc.arc_id, 'description', v)}
                              testId="editable-field"
                            />
                          </div>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 text-xs w-16 shrink-0">importance</span>
                          <div className="flex-1 min-w-0">
                            <EditableField
                              value={pendingVal('arcs', arc.arc_id, 'importance', arc.importance) != null
                                ? String(pendingVal('arcs', arc.arc_id, 'importance', arc.importance))
                                : null}
                              onEdit={(v) => editById('arcs', arc.arc_id, 'importance', v ? parseInt(v, 10) : null)}
                              testId="editable-field"
                            />
                          </div>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 text-xs w-16 shrink-0">status</span>
                          <div className="flex-1 min-w-0">
                            <EditableField
                              value={pendingVal('arc_state', arc.arc_id, 'status', arc.status)}
                              onEdit={(v) => editById('arc_state', arc.arc_id, 'status', v)}
                              testId="editable-field"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Row label="arcs"><Unset /></Row>
              )}
            </div>
          </div>

          {/* ── CHARACTERS ── */}
          <div>
            <SectionHeader label="CHARACTERS" color="text-purple-400" />
            {roomState.characters?.length > 0 ? (
              <div className="space-y-2 pl-2">
                {roomState.characters.map((char) => {
                  const isOpen = expandedChars[char.id];
                  const cid = char.id;
                  return (
                    <div key={cid} className="border border-gray-800 rounded">
                      <button
                        className="w-full flex items-center space-x-2 px-2 py-1 text-left hover:bg-gray-800"
                        onClick={() => toggleChar(cid)}
                        data-testid="character-toggle"
                      >
                        <span className="text-gray-400 text-xs">{isOpen ? '▼' : '▶'}</span>
                        <span className="text-purple-300 text-xs font-semibold">{char.name}</span>
                        {char.role && <span className="text-gray-500 text-xs">({char.role})</span>}
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-2 space-y-1">
                          {entityFields('characters', cid, char, ['name', 'role', 'description'])}
                          {/* _state fields */}
                          {[
                            ['goal',      'current_goal',     false],
                            ['emotion',   'emotion',          false],
                            ['posture',   'public_posture',   false],
                            ['knowledge', 'knowledge_summary', true],
                          ].map(([label, field, multi]) => (
                            <Row key={field} label={label}>
                              <EditableField
                                value={pendingVal('character_state', cid, field, char[field])}
                                onEdit={(v) => editById('character_state', cid, field, v)}
                                multiline={multi} testId="editable-field"
                              />
                            </Row>
                          ))}
                          <Row label="level">
                            <EditableField
                              value={pendingVal('character_state', cid, 'emotion_level', char.emotion_level) != null
                                ? String(pendingVal('character_state', cid, 'emotion_level', char.emotion_level))
                                : null}
                              onEdit={(v) => editById('character_state', cid, 'emotion_level', v ? parseInt(v, 10) : null)}
                              testId="editable-field"
                            />
                          </Row>
                          {/* entity fields (also sync to character_state) */}
                          <Row label="traits">
                            <EditableField
                              value={pendingVal('characters', cid, 'personality_traits', char.personality_traits)?.join(', ') }
                              onEdit={(v) => editById('characters', cid, 'personality_traits', v.split(',').map(s => s.trim()).filter(Boolean))}
                              testId="editable-field"
                            />
                          </Row>
                          <Row label="speech">
                            <EditableField
                              value={pendingVal('characters', cid, 'speech_patterns', char.speech_patterns)}
                              onEdit={(v) => editById('characters', cid, 'speech_patterns', v)}
                              testId="editable-field"
                            />
                          </Row>
                          <Row label="motive">
                            <EditableField
                              value={pendingVal('characters', cid, 'private_motive', char.private_motive)}
                              onEdit={(v) => editById('characters', cid, 'private_motive', v)}
                              multiline testId="editable-field"
                            />
                          </Row>
                          <Row label="public mask">
                            <EditableField
                              value={pendingVal('characters', cid, 'public_mask', char.public_mask)}
                              onEdit={(v) => editById('characters', cid, 'public_mask', v)}
                              testId="editable-field"
                            />
                          </Row>
                          <Row label="backstory">
                            <EditableField
                              value={pendingVal('characters', cid, 'backstory', char.backstory)}
                              onEdit={(v) => editById('characters', cid, 'backstory', v)}
                              multiline testId="editable-field"
                            />
                          </Row>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pl-2 text-xs"><Unset /></div>
            )}
          </div>

          {/* ── RELATIONSHIPS ── */}
          {roomState.relationships?.length > 0 && (
            <div>
              <SectionHeader label="RELATIONSHIPS" color="text-pink-400" />
              <div className="space-y-3 pl-2">
                {roomState.relationships.map((rel, i) => {
                  const charA = roomState.characters?.find(c => c.id === rel.character_a_id);
                  const charB = roomState.characters?.find(c => c.id === rel.character_b_id);
                  const nameA = charA?.name || rel.character_a_id.slice(0, 8);
                  const nameB = charB?.name || rel.character_b_id.slice(0, 8);
                  return (
                    <div key={i} className="border border-gray-800 rounded p-2 space-y-1">
                      <div className="text-pink-300 text-xs font-semibold mb-1">{nameA} ↔ {nameB}</div>
                      {['trust', 'suspicion', 'affection', 'fear', 'debt'].map(field => (
                        <Row key={field} label={field}>
                          <EditableField
                            value={relPendingVal(rel.character_a_id, rel.character_b_id, field, rel[field]) != null
                              ? String(relPendingVal(rel.character_a_id, rel.character_b_id, field, rel[field]))
                              : null}
                            onEdit={(v) => editRelationship(rel.character_a_id, rel.character_b_id, field, v ? parseInt(v, 10) : null)}
                            testId="editable-field"
                          />
                        </Row>
                      ))}
                      <Row label="summary">
                        <EditableField
                          value={relPendingVal(rel.character_a_id, rel.character_b_id, 'summary', rel.summary)}
                          onEdit={(v) => editRelationship(rel.character_a_id, rel.character_b_id, 'summary', v)}
                          multiline testId="editable-field"
                        />
                      </Row>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── LOCATION ── */}
          <div>
            <SectionHeader label="LOCATION" color="text-yellow-300" />
            {(roomState.locations || (roomState.location ? [roomState.location] : [])).map(location => (
              <div key={location.location_id} className="space-y-1 pl-2 mb-3 border border-gray-800 rounded p-2">
                <div className="text-yellow-300 text-xs">
                  {location.location_id === roomState.location?.location_id ? 'Current scene location' : 'Room location'}
                </div>
                {entityFields('locations', location.location_id, location, ['name', 'description', 'atmosphere'])}
                {entityFields('location_state', location.location_id, location, ['current_mood', 'notable_events'])}
                <Row label="affordances">
                  <EditableField
                    value={(pendingVal('locations', location.location_id, 'affordances', location.affordances) || []).join(', ')}
                    onEdit={v => editById('locations', location.location_id, 'affordances', v.split(',').map(s => s.trim()).filter(Boolean))}
                    testId={`locations-${location.location_id}-affordances`} />
                </Row>
              </div>
            ))}
            {!roomState.location && <div className="text-xs text-gray-400">No location linked to the current scene.</div>}
          </div>

          {/* ── PROPS ── */}
          {roomState.props?.length > 0 && (
            <div>
              <SectionHeader label="PROPS" color="text-orange-400" />
              <div className="space-y-2 pl-2">
                {roomState.props.map((prop, i) => (
                  <div key={i} className="space-y-1">
                    {entityFields('props', prop.prop_id, prop, ['name', 'description', 'narrative_significance'])}
                    <Row label="condition">
                      <EditableField
                        value={pendingVal('prop_state', prop.prop_id, 'condition', prop.condition)}
                        onEdit={(v) => editById('prop_state', prop.prop_id, 'condition', v)}
                        testId="editable-field"
                      />
                    </Row>
                    <Row label="is known">
                      <EditableField
                        value={pendingVal('prop_state', prop.prop_id, 'is_known', prop.is_known) != null
                          ? String(pendingVal('prop_state', prop.prop_id, 'is_known', prop.is_known))
                          : null}
                        onEdit={(v) => editById('prop_state', prop.prop_id, 'is_known', v === 'true')}
                        testId="editable-field"
                      />
                    </Row>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── HISTORY ── */}
          <div>
            <SectionHeader label="HISTORY" color="text-gray-400" />
            <div className="pl-2 space-y-2">
              <div>
                <span className="text-gray-500 text-xs">Recent arc beats</span>
                {roomState.recent_arc_beats?.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {roomState.recent_arc_beats.slice(0, 5).map((b, i) => (
                      <li key={i} className="text-gray-500 text-xs truncate">{b.beat || b.description || JSON.stringify(b)}</li>
                    ))}
                  </ul>
                ) : <div className="text-xs mt-0.5"><Unset /></div>}
              </div>
              <div>
                <span className="text-gray-500 text-xs">Recent scene summaries</span>
                {roomState.recent_scene_summaries?.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {roomState.recent_scene_summaries.slice(0, 3).map((s, i) => (
                      <li key={i} className="text-gray-500 text-xs truncate">{typeof s === 'string' ? s : s.scene_summary || JSON.stringify(s)}</li>
                    ))}
                  </ul>
                ) : <div className="text-xs mt-0.5"><Unset /></div>}
              </div>
            </div>
          </div>

          {/* ── CANON RULES ── */}
          {roomState.canon_state?.length > 0 && (
            <div>
              <SectionHeader label="CANON RULES" color="text-red-400" />
              <div className="space-y-2 pl-2">
                {roomState.canon_state.map((rule) => (
                  <div key={rule.canon_rule_id} className="border border-gray-800 rounded p-2 space-y-1">
                    <div className="flex items-start space-x-2">
                      <span className="text-gray-500 text-xs w-16 shrink-0">key</span>
                      <div className="flex-1 min-w-0">
                        <EditableField
                          value={pendingVal('canon_rules', rule.canon_rule_id, 'rule_key', rule.rule_key)}
                          onEdit={(v) => editById('canon_rules', rule.canon_rule_id, 'rule_key', v)}
                          testId="editable-field"
                        />
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-gray-500 text-xs w-16 shrink-0">value</span>
                      <div className="flex-1 min-w-0">
                        <EditableField
                          value={pendingVal('canon_rules', rule.canon_rule_id, 'rule_value', rule.rule_value)}
                          onEdit={(v) => editById('canon_rules', rule.canon_rule_id, 'rule_value', v)}
                          multiline testId="editable-field"
                        />
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-gray-500 text-xs w-16 shrink-0">description</span>
                      <div className="flex-1 min-w-0">
                        <EditableField
                          value={pendingVal('canon_rules', rule.canon_rule_id, 'description', rule.description)}
                          onEdit={(v) => editById('canon_rules', rule.canon_rule_id, 'description', v)}
                          multiline testId="editable-field"
                        />
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-gray-500 text-xs w-16 shrink-0">priority</span>
                      <div className="flex-1 min-w-0">
                        <EditableField
                          value={pendingVal('canon_rules', rule.canon_rule_id, 'priority', rule.priority) != null
                            ? String(pendingVal('canon_rules', rule.canon_rule_id, 'priority', rule.priority))
                            : null}
                          onEdit={(v) => editById('canon_rules', rule.canon_rule_id, 'priority', v ? parseInt(v, 10) : null)}
                          testId="editable-field"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <details className="text-xs text-gray-200">
            <summary className="cursor-pointer">Complete room data (read-only)</summary>
            <pre className="whitespace-pre-wrap break-words mt-2">{JSON.stringify(roomState, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default StoryRoomEditor;
